<template>
    <v-data-table
        :headers="headers"
        :items="sortedLocalTimeEntries"
        class="elevation-2"
        style="max-height: 100%; overflow-y: auto; scrollbar-width: none"
        hide-default-footer
        items-per-page="-1"
        fixed-header
    >
        <!-- Custom Row Rendering -->
        <template v-slot:body="{ items }">
            <tr
                v-for="item in items"
                :key="item.id"
                :class="{ 'row-disabled': !item.endTime }"
            >
                <td>
                    <v-autocomplete
                        :disabled="
                            !isSelectedDateToday &&
                            !this.getRights.canModifyPreviousDateEntries
                        "
                        v-model="item.projectId"
                        :items="sortedProjects"
                        item-value="projectId"
                        item-title="name"
                        placeholder="Select Project"
                        flat
                        density="compact"
                        variant="plain"
                        hide-details="true"
                        hide-no-data
                        hide-icon="true"
                        auto-select-first
                        @focus="onProjectAutocompleteFocus"
                        @update:modelValue="
                            (newValue) => updateProject(item.id, newValue)
                        "
                        :style="{
                            maxWidth: $vuetify.display.smAndDown
                                ? '100px'
                                : '70%',
                        }"
                    />
                    <ul
                        v-if="item.description && item.description.length"
                        class="ml-2 mb-2 pl-4"
                    >
                        <li>
                            {{ item.description }}
                        </li>
                    </ul>
                </td>
                <!--Interval  with Inline Editing -->
                <td>
                    <!-- Conditional rendering based on project name and canEditLunchBreak -->
                    <div
                        v-if="
                            item.projectId === 'SYSTEM_LUNCH-BREAK' &&
                            !canEditLunchBreak
                        "
                        :class="{ 'disabled-style': !isSelectedDateToday }"
                        class="d-flex justify-start direction-row align-center flex-row ga-3"
                    >
                        <span>{{ item.startTimeFormatted }} </span> -
                        <span>{{ item.endTimeFormatted ?? "Ongoing" }}</span>
                    </div>
                    <div
                        v-else
                        class="d-flex justify-start align-center flex-row ga-3 mt-n2"
                    >
                        <v-menu
                            v-model="item.startTimePickerVisible"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ props }">
                                <v-text-field
                                    :disabled="
                                        !isSelectedDateToday &&
                                        !this.getRights
                                            .canModifyPreviousDateEntries
                                    "
                                    v-bind="props"
                                    v-model="item.startTimeFormatted"
                                    readonly
                                    hide-details="true"
                                    @click="item.startTimePickerVisible = true"
                                    variant="plain"
                                    min-width="40px"
                                    max-width="40px"
                                    style="--v-input-control-font-size: 14px"
                                ></v-text-field>
                            </template>
                            <v-time-picker
                                v-model="item.startTimeFormatted"
                                @update:modelValue="
                                    (newValue) =>
                                        updateTimeField(
                                            item.id,
                                            'startTime',
                                            newValue
                                        )
                                "
                                format="24hr"
                                full-width
                                :scrollable="false"
                            ></v-time-picker>
                        </v-menu>
                        <span class="pt-4">-</span>
                        <v-menu
                            v-model="item.endTimePickerVisible"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ props }">
                                <v-text-field
                                    :disabled="
                                        (!isSelectedDateToday &&
                                            !this.getRights
                                                .canModifyPreviousDateEntries) ||
                                        isEndTimeDisabled(item)
                                    "
                                    v-bind="props"
                                    v-model="item.endTimeFormatted"
                                    readonly
                                    hide-details="true"
                                    @click="item.endTimePickerVisible = true"
                                    variant="plain"
                                    min-width="40px"
                                    max-width="55px"
                                ></v-text-field>
                            </template>
                            <v-time-picker
                                v-model="item.endTimeFormatted"
                                @update:modelValue="
                                    (newValue) =>
                                        updateTimeField(
                                            item.id,
                                            'endTime',
                                            newValue
                                        )
                                "
                                :disabled="isEndTimeDisabled(item)"
                                format="24hr"
                                full-width
                                :scrollable="false"
                            ></v-time-picker>
                        </v-menu>
                    </div>
                </td>
                <td
                    @click.stop="
                        $vuetify.display.smAndDown &&
                            activateMenuOnSmallScreen(item)
                    "
                >
                    <v-menu
                        v-model="item.menuVisible"
                        bottom
                        :open-on-click="$vuetify.display.smAndDown"
                    >
                        <template v-slot:activator="{ props }">
                            <div
                                v-bind="props"
                                class="d-flex justify-space-between align-center ga-4 mt-1"
                            >
                                <div>
                                    <span>{{ item.hours }}</span>

                                    <!-- Tooltip -->
                                    <span
                                        v-if="item.overtime"
                                        class="ml-2"
                                        style="
                                            display: inline-flex;
                                            align-items: center;
                                        "
                                    >
                                        <v-chip
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            aria-label="Overtime"
                                            label
                                        >
                                            OT
                                        </v-chip>
                                        <v-tooltip
                                            activator="parent"
                                            text="Overtime"
                                            location="top"
                                            open-delay="150"
                                        />
                                    </span>
                                </div>
                                <v-btn
                                    :disabled="
                                        !isSelectedDateToday &&
                                        !this.getRights
                                            .canModifyPreviousDateEntries
                                    "
                                    icon
                                    flat
                                    :class="{
                                        'unstyled-button':
                                            $vuetify.display.smAndDown,
                                    }"
                                    @click.stop="activateMenu(item)"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </div>
                        </template>
                        <v-list>
                            <v-list-item
                                @click.stop.prevent="
                                    handleAction('before', item.id)
                                "
                            >
                                <v-list-item-title
                                    >Add Before</v-list-item-title
                                >
                            </v-list-item>
                            <v-list-item
                                @click.stop.prevent="
                                    handleAction('after', item.id)
                                "
                            >
                                <v-list-item-title>Add After</v-list-item-title>
                            </v-list-item>
                            <!-- Mark as Overtime (show only when not OT) -->
                            <v-list-item
                                v-if="
                                    item.projectId !== 'SYSTEM_LUNCH-BREAK' &&
                                    !item.overtime
                                "
                                :disabled="
                                    !isSelectedDateToday &&
                                    !getRights?.canModifyPreviousDateEntries
                                "
                                @click.stop.prevent="setOvertime(item, true)"
                            >
                                <v-list-item-title
                                    >Mark as Overtime</v-list-item-title
                                >
                            </v-list-item>

                            <!-- Unmark Overtime (show only when OT) -->
                            <v-list-item
                                v-if="
                                    item.projectId !== 'SYSTEM_LUNCH-BREAK' &&
                                    item.overtime
                                "
                                :disabled="
                                    !isSelectedDateToday &&
                                    !getRights?.canModifyPreviousDateEntries
                                "
                                @click.stop.prevent="setOvertime(item, false)"
                            >
                                <v-list-item-title
                                    >Unmark Overtime</v-list-item-title
                                >
                            </v-list-item>
                            <!-- Add/Edit Description -->
                            <v-list-item
                                v-if="item.projectId !== 'SYSTEM_LUNCH-BREAK'"
                                :disabled="
                                    !isSelectedDateToday &&
                                    !getRights?.canModifyPreviousDateEntries
                                "
                                @click.stop.prevent="
                                    openDescriptionDialog(item)
                                "
                            >
                                <v-list-item-title>
                                    {{
                                        item.description
                                            ? "Edit Description"
                                            : "Add Description"
                                    }}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="
                                    isLastEntry(item) &&
                                    item.projectId !== 'SYSTEM_LUNCH-BREAK' &&
                                    item.endTime &&
                                    !item.endOfSession
                                "
                                :disabled="
                                    !isSelectedDateToday &&
                                    !getRights?.canModifyPreviousDateEntries
                                "
                                @click.stop.prevent="markAsEndOfDay(item)"
                            >
                                <v-list-item-title
                                    >Mark as End of Day</v-list-item-title
                                >
                            </v-list-item>
                            <v-list-item
                                v-if="
                                    isLastEntry(item) &&
                                    item.projectId !== 'SYSTEM_LUNCH-BREAK' &&
                                    item.endOfSession
                                "
                                :disabled="
                                    !isSelectedDateToday &&
                                    !getRights?.canModifyPreviousDateEntries
                                "
                                @click.stop.prevent="unmarkEndOfDay(item)"
                            >
                                <v-list-item-title
                                    >Unmark End of Day</v-list-item-title
                                >
                            </v-list-item>
                            <v-list-item
                                v-if="canEditLunchBreak"
                                @click="deleteTimeEntry(item.id)"
                            >
                                <v-list-item-title
                                    >Delete Entry</v-list-item-title
                                >
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </td>
            </tr>
        </template>

        <!-- Footer -->
        <template v-slot:body.append>
            <tr>
                <td>Total hours:</td>
                <td></td>
                <td colspan="3">{{ totalTimeWorked }}</td>
            </tr>
        </template>
    </v-data-table>

    <!-- Description Edit Dialog -->
    <v-dialog v-model="descriptionDialog.visible" max-width="500px" persistent>
        <v-card>
            <v-card-title class="bg-primary text-white">
                {{
                    descriptionDialog.text
                        ? "Edit Description"
                        : "Add Description"
                }}
            </v-card-title>
            <v-card-text class="pb-0">
                <v-textarea
                    v-model="descriptionDialog.text"
                    label="Description"
                    rows="3"
                    auto-grow
                    variant="outlined"
                    placeholder="Enter description..."
                ></v-textarea>
                <v-card-actions class="pr-0 d-flex justify-space-between">
                    <v-btn text @click="closeDescriptionDialog"> Cancel </v-btn>
                    <v-btn color="primary" text @click="saveDescription">
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { calculateTotalTimeWorked, isBelowThreshold } from "@/utils/utils";
import { mapGetters } from "vuex";
import dayjs from "@/plugins/dayjs";

export default {
    props: {
        timeEntries: {
            type: Array,
            default: () => [],
        },
        totalTimeWorked: {
            type: String,
            required: true,
        },
        projects: {
            type: Array,
            default: () => [],
        },
        selectedDate: {
            type: [Date, String, Array],
            required: true,
        },
        user: {
            type: Object,
            required: true, // The user whose time entries are being edited
        },
    },
    data() {
        return {
            headers: [
                { title: "Project", value: "projectName" },
                {
                    title: "Interval",
                    value: "interval",
                    text: "center",
                    maxWidth: "200px",
                },
                { title: "Hours", value: "hours" },
            ],
            localTimeEntries: [],
            descriptionDialog: {
                visible: false,
                entryId: null,
                text: "",
            },
        };
    },
    computed: {
        isNrOfItems() {
            return isBelowThreshold(this.localTimeEntries);
        },

        /**
         * Checks if the currently selected date (or any in the selected range) matches today's date.
         *
         * - Supports both single-date mode and range mode (array of Date objects).
         * - Uses `dayjs` for date comparison at the 'day' level (ignores time).
         *
         * @returns {boolean} True if the selected date (or any date in the range) is today; otherwise, false.
         */
        isSelectedDateToday() {
            const today = dayjs();

            if (Array.isArray(this.selectedDate)) {
                return this.selectedDate.some((date) =>
                    dayjs(date).isSame(today, "day")
                );
            } else {
                return dayjs(this.selectedDate).isSame(today, "day");
            }
        },
        canEditLunchBreak() {
            return this.getRights?.editLunchBreak || false;
        },

        /**
         * Calculates the total time worked for all editable time entries.
         * @returns {string} Total time worked in a formatted string.
         */
        editableTimeEntriesTotalTimeWorked() {
            return calculateTotalTimeWorked(this.localTimeEntries);
        },
        ...mapGetters({
            getRights: "getRights",
        }),
        sortedLocalTimeEntries() {
            return [...this.localTimeEntries].sort(
                (a, b) => a.startTime - b.startTime
            );
        },
        /** Project picker options A–Z by name. */
        sortedProjects() {
            return [...(this.projects || [])].sort((a, b) =>
                String(a.name || "").localeCompare(String(b.name || ""))
            );
        },
    },
    watch: {
        timeEntries: {
            immediate: true,
            handler(newEntries) {
                this.localTimeEntries =
                    this.formatPlainEntriesForDataTable(newEntries);
            },
        },
    },
    methods: {
        async addFirstEntry() {
            const anchorDate = Array.isArray(this.selectedDate)
                ? this.selectedDate[0]
                : this.selectedDate;

            // default 08:30–08:31 local on that date
            const startTime = dayjs(anchorDate)
                .hour(8)
                .minute(30)
                .second(0)
                .millisecond(0)
                .valueOf();
            const endTime = startTime + 60 * 1000;

            const userId = this.user?.uid;
            if (!userId) return;

            const newEntry = {
                projectId: null,
                startTime,
                endTime,
                overtime: false,
            };

            try {
                await this.$store.dispatch("addTimeEntry", {
                    userId,
                    newEntry,
                });
                // Vuex listeners will refresh the visible entries
            } catch (e) {
                console.error("Failed to add first entry:", e);
            }
        },
        /**
         * Sets (or clears) the overtime flag for a single time entry.
         *
         * Performs an optimistic UI update on the row and persists the change
         * via the Vuex action `updateTimeEntryOvertime`. If the write fails,
         * the local value is reverted. Lunch Break entries are ignored.
         *
         * @param {Object} item - The row item representing the time entry.
         * @param {string} item.id - Time entry id (DB key).
         * @param {string} item.projectId - Project id the entry belongs to.
         * @param {boolean} item.overtime - Current overtime flag for this entry.
         * @param {boolean} value - Target overtime state (`true` to mark, `false` to unmark).
         * @returns {Promise<void>} Resolves when the update attempt completes (success or failure).
         */
        async setOvertime(item, value) {
            if (item.projectId === "SYSTEM_LUNCH-BREAK") return;

            const userId = this.user?.uid;
            if (!userId) return;

            const previous = item.overtime;
            item.overtime = value;

            try {
                await this.$store.dispatch("updateTimeEntryOvertime", {
                    userId,
                    entryId: item.id,
                    overtime: value,
                });
            } catch (err) {
                console.error("Failed to update overtime:", err);
                item.overtime = previous;
            } finally {
                item.menuVisible = false;
            }
        },
        /**
         * True when the item is the chronologically last entry in the table.
         * @param {Object} item
         * @returns {boolean}
         */
        isLastEntry(item) {
            const sorted = this.sortedLocalTimeEntries;
            if (!sorted.length) return false;
            return sorted[sorted.length - 1].id === item.id;
        },
        /**
         * Marks the last entry of the workday as end-of-session.
         * Clears the flag on any other entries for the selected day first.
         * @param {Object} item
         */
        async markAsEndOfDay(item) {
            const userId = this.user?.uid;
            if (!userId || !this.isLastEntry(item)) return;

            try {
                for (const entry of this.localTimeEntries) {
                    if (entry.id !== item.id && entry.endOfSession) {
                        entry.endOfSession = false;
                        await this.$store.dispatch("updateTimeEntryFlag", {
                            userId,
                            entryId: entry.id,
                            endOfSession: false,
                        });
                    }
                }

                item.endOfSession = true;
                await this.$store.dispatch("updateTimeEntryFlag", {
                    userId,
                    entryId: item.id,
                    endOfSession: true,
                });
            } catch (err) {
                console.error("Failed to mark entry as end of day:", err);
                item.endOfSession = false;
            } finally {
                item.menuVisible = false;
            }
        },
        /**
         * Clears the end-of-session flag from the last entry.
         * @param {Object} item
         */
        async unmarkEndOfDay(item) {
            const userId = this.user?.uid;
            if (!userId || !this.isLastEntry(item)) return;

            const previous = item.endOfSession;
            item.endOfSession = false;

            try {
                await this.$store.dispatch("updateTimeEntryFlag", {
                    userId,
                    entryId: item.id,
                    endOfSession: false,
                });
            } catch (err) {
                console.error("Failed to unmark end of day:", err);
                item.endOfSession = previous;
            } finally {
                item.menuVisible = false;
            }
        },
        /**
         * Opens the description edit dialog for a specific entry.
         * @param {Object} item - The time entry item to edit description for.
         */
        openDescriptionDialog(item) {
            this.descriptionDialog.entryId = item.id;
            this.descriptionDialog.text = item.description || "";
            this.descriptionDialog.visible = true;
            item.menuVisible = false;
        },
        /**
         * Closes the description dialog without saving.
         */
        closeDescriptionDialog() {
            this.descriptionDialog.visible = false;
            this.descriptionDialog.entryId = null;
            this.descriptionDialog.text = "";
        },
        /**
         * Saves the description for the current entry to RTDB.
         */
        async saveDescription() {
            if (this.descriptionDialog.entryId === null) return;

            const userId = this.user?.uid;
            if (!userId) return;

            const entryIndex = this.localTimeEntries.findIndex(
                (entry) => entry.id === this.descriptionDialog.entryId
            );
            if (entryIndex === -1) {
                this.closeDescriptionDialog();
                return;
            }

            const entry = this.localTimeEntries[entryIndex];
            const previousDescription = entry.description;
            const newDescription = this.descriptionDialog.text.trim() || null;

            entry.description = newDescription;

            try {
                await this.$store.dispatch("updateTimeEntryDescription", {
                    userId,
                    entryId: this.descriptionDialog.entryId,
                    description: newDescription,
                });
                this.closeDescriptionDialog();
            } catch (err) {
                console.error("Failed to update description:", err);
                entry.description = previousDescription;
            }
        },
        /**
         * Checks if the endTime of an item is disabled.
         * @param {Object} item - The item to check.
         * @returns {boolean} True if the endTime is disabled.
         */
        isEndTimeDisabled(item) {
            // Use chronological order to find the “next” entry as shown in the table
            const sorted = [...this.localTimeEntries].sort(
                (a, b) => a.startTime - b.startTime
            );
            const idx = sorted.findIndex((e) => e.id === item.id);
            const nextItem = sorted[idx + 1];

            // Disable if a next entry exists AND that next entry is ongoing (no endTime)
            return !!(nextItem && !nextItem.endTime);
        },
        /**
         * Activates the menu for a given item only on small screens.
         * Checks if the screen size meets the "small and down" condition before toggling the menu.
         *
         * @param {Object} item - The item for which the menu is being activated.
         */
        activateMenuOnSmallScreen(item) {
            if (this.$vuetify.display.smAndDown) {
                this.activateMenu(item);
            }
        },

        /**
         * Toggles the visibility of the menu for a given item.
         *
         * @param {Object} item - The item for which the menu's visibility should be toggled.
         */
        activateMenu(item) {
            item.menuVisible = !item.menuVisible;
        },
        /**
         * Deletes a time entry from the local entries and the database.
         * Updates the `startTime` of the next entry if the deleted entry is not the last one.
         *
         * @param {number} index - The index of the entry to delete in the `localTimeEntries` array.
         */
        async deleteTimeEntry(entryId) {
            try {
                const userId = this.user?.uid;
                if (!userId) throw new Error("User not logged in");

                // Work in the same chronological order as the table
                const sorted = [...this.localTimeEntries].sort(
                    (a, b) => a.startTime - b.startTime
                );
                const idx = sorted.findIndex((e) => e.id === entryId);
                if (idx === -1) return;

                const entryToDelete = sorted[idx];
                const isFirst = idx === 0;
                const isLast = idx === sorted.length - 1;

                // If we delete the last entry and there is a previous one,
                // mark the previous as endOfSession = true (carry the boundary).
                if (isLast && !isFirst) {
                    const prev = sorted[idx - 1];
                    prev.endOfSession = true;
                    await this.$store.dispatch("updateTimeEntryFlag", {
                        userId,
                        entryId: prev.id,
                        endOfSession: true,
                    });
                }

                // Delete from Firebase
                await this.$store.dispatch("deleteTimeEntry", {
                    userId,
                    entryId: entryToDelete.id,
                });

                // Remove locally (by id)
                const localIdx = this.localTimeEntries.findIndex(
                    (e) => e.id === entryToDelete.id
                );
                if (localIdx !== -1) this.localTimeEntries.splice(localIdx, 1);
            } catch (error) {
                console.error("Error deleting time entry:", error);
            }
        },
        /**
         * Convert a local time-of-day string ("HH:mm") into a UTC timestamp,
         * anchoring it to the local calendar date derived from `baseTimestamp`.
         *
         * @param {string} timeString - Time of day in "HH:mm" (interpreted in local timezone).
         * @param {number} baseTimestamp - Epoch ms; only its local date is used as the anchor day.
         * @returns {number} UTC timestamp in milliseconds.
         */
        formatLocalTimeToUtc(timeString, baseTimestamp) {
            const [hours, minutes] = timeString.split(":").map(Number);
            return dayjs(baseTimestamp)
                .set("hour", hours)
                .set("minute", minutes)
                .set("second", 0)
                .set("millisecond", 0)
                .utc()
                .valueOf();
        },

        /**
         * Format a UTC timestamp into a local "HH:mm" string for display.
         *
         * @param {number} timestamp - UTC timestamp in milliseconds.
         * @returns {string} Local time string in "HH:mm" (24-hour).
         */
        formatUtcTimeToLocal(timestamp) {
            return dayjs.utc(timestamp).local().format("HH:mm");
        },

        /**
         * Add exactly one **local** calendar day to a UTC instant and return UTC ms.
         * (We go to local time, add 1 day, then go back to UTC.)
         *
         * @param {number} utcMs - Original UTC timestamp in milliseconds.
         * @returns {number} New UTC timestamp after adding one local day.
         */
        addOneLocalDay(utcMs) {
            return dayjs.utc(utcMs).local().add(1, "day").utc().valueOf();
        },

        /**
         * Update startTime or endTime from a picked local "HH:mm", keeping entries valid
         * and continuous with neighbors (unless an endOfSession boundary exists).
         * - For endTime edits: anchor to start's local date; if picked time ≤ start's "HH:mm",
         *   treat as next local day (cross-midnight).
         * - Validates current row and neighbor rows to avoid zero/negative durations.
         * - Persists current and any affected neighbor row(s).
         *
         * @param {number} index - Row index in `localTimeEntries`.
         * @param {"startTime"|"endTime"} field - Which field is being updated.
         * @param {string} newValue - Picked local time string, "HH:mm".
         * @returns {Promise<void>}
         */
        async updateTimeField(entryId, field, newValue) {
            // Chronological array for neighbor lookups
            const sorted = [...this.localTimeEntries].sort(
                (a, b) => a.startTime - b.startTime
            );
            const idx = sorted.findIndex((e) => e.id === entryId);
            if (idx === -1) return;

            const currentItem = sorted[idx];
            const originalValue = currentItem[field];

            // ---- Convert "HH:mm" → UTC (anchored correctly) ----
            let newTimeUTC;
            if (field === "endTime") {
                // Anchor to the local date of the START time
                newTimeUTC = this.formatLocalTimeToUtc(
                    newValue,
                    currentItem.startTime
                );

                // If chosen time-of-day is <= start's time-of-day, treat as next local day (cross-midnight)
                const startHHmm = this.formatUtcTimeToLocal(
                    currentItem.startTime
                );
                if (newValue <= startHHmm) {
                    newTimeUTC = this.addOneLocalDay(newTimeUTC);
                }
            } else {
                // field === "startTime" → keep the same local date as the original start
                newTimeUTC = this.formatLocalTimeToUtc(
                    newValue,
                    currentItem.startTime
                );
            }

            // --- Validations (current row) ---
            if (
                field === "startTime" &&
                currentItem.endTime &&
                newTimeUTC >= currentItem.endTime
            ) {
                alert(
                    "Start time cannot make the current entry have 0 or negative duration."
                );
                currentItem.startTimeFormatted =
                    this.formatUtcTimeToLocal(originalValue);
                currentItem.startTimePickerVisible = false;
                return;
            }
            if (field === "endTime" && newTimeUTC <= currentItem.startTime) {
                alert(
                    "End time cannot make the current entry have 0 or negative duration."
                );
                currentItem.endTimeFormatted =
                    this.formatUtcTimeToLocal(originalValue);
                currentItem.endTimePickerVisible = false;
                return;
            }

            // ---- Validations (neighbors by chronological order) ----
            const prevItem = sorted[idx - 1];
            const nextItem = sorted[idx + 1];

            if (
                field === "endTime" &&
                nextItem &&
                nextItem.endTime &&
                newTimeUTC >= nextItem.endTime
            ) {
                alert(
                    "End time cannot force the next entry to have 0 or negative duration."
                );
                currentItem.endTimeFormatted =
                    this.formatUtcTimeToLocal(originalValue);
                currentItem.endTimePickerVisible = false;
                return;
            }
            if (
                field === "startTime" &&
                prevItem &&
                newTimeUTC <= prevItem.startTime
            ) {
                alert(
                    "Start time cannot force the previous entry to have 0 or negative duration."
                );
                currentItem.startTimeFormatted =
                    this.formatUtcTimeToLocal(originalValue);
                currentItem.startTimePickerVisible = false;
                return;
            }

            // --- Apply change to current row ---
            currentItem[field] = newTimeUTC;
            currentItem[`${field}Formatted`] =
                this.formatUtcTimeToLocal(newTimeUTC);
            this.recalculateDuration(currentItem);

            // --- Keep continuity with neighbors (within same session) ---
            if (field === "startTime" && prevItem && !prevItem.endOfSession) {
                prevItem.endTime = newTimeUTC;
                prevItem.endTimeFormatted =
                    this.formatUtcTimeToLocal(newTimeUTC);
                this.recalculateDuration(prevItem);
                await this.updateEntryInFirebase(prevItem);
            } else if (
                field === "endTime" &&
                nextItem &&
                !currentItem.endOfSession
            ) {
                nextItem.startTime = newTimeUTC;
                nextItem.startTimeFormatted =
                    this.formatUtcTimeToLocal(newTimeUTC);
                this.recalculateDuration(nextItem);
                await this.updateEntryInFirebase(nextItem);
            }

            // Persist current
            await this.updateEntryInFirebase(currentItem);

            // Close pickers
            if (field === "startTime")
                currentItem.startTimePickerVisible = false;
            if (field === "endTime") currentItem.endTimePickerVisible = false;
        },
        /**
         * Recalculates the duration (hours) for a given entry.
         * @param {Object} entry - The time entry to recalculate.
         */
        recalculateDuration(entry) {
            const durationMs = entry.endTime - entry.startTime;
            const totalMinutes = Math.ceil(durationMs / (1000 * 60));
            const h = Math.floor(totalMinutes / 60);
            const m = totalMinutes % 60;
            entry.hours = h > 0 ? `${h}h ${m}min` : `${m}min`;
        },

        /**
         * Updates an entry in Firebase.
         * @param {Object} entry - The entry to update.
         */
        async updateEntryInFirebase(entry) {
            try {
                await this.$store.dispatch("updateTimeEntryInterval", {
                    userId: this.user.uid,
                    entryId: entry.id,
                    startTime: entry.startTime,
                    endTime: entry.endTime,
                });
            } catch (error) {
                console.error("Failed to update entry in Firebase:", error);
            }
        },

        /**
         * Highlight the current project name on focus so the next keystroke
         * replaces it (type-to-search without deleting first).
         */
        onProjectAutocompleteFocus(event) {
            const input =
                event?.target?.tagName === "INPUT"
                    ? event.target
                    : document.activeElement;
            if (input?.tagName === "INPUT" && input.select) {
                this.$nextTick(() => input.select());
            }
        },

        /**
         * Updates the project ID of a specific time entry in the database.
         * @param {string} entryId - ID of the entry to update.
         * @param {string} newProjectId - New project ID to assign.
         * @async
         */
        async updateProject(entryId, newProjectId) {
            try {
                const userId = this.user?.uid;
                if (!userId) throw new Error("User not logged in");

                await this.$store.dispatch("updateTimeEntryProject", {
                    userId,
                    entryId,
                    newProjectId,
                });

                const entryIndex = this.localTimeEntries.findIndex(
                    (entry) => entry.id === entryId
                );
                if (entryIndex !== -1) {
                    this.localTimeEntries[entryIndex].projectId = newProjectId;
                }
            } catch (error) {
                console.error("Error updating projectId:", error);
            }
        },

        /**
         * Handles adding a new time entry before or after a selected entry.
         * Adjusts the timing of neighboring entries and updates the database accordingly.
         *
         * @param {string} position - Either "before" or "after", indicating where the new entry should be added.
         * @param {Object} item - The current time entry item.
         * @param {number} index - The index of the current entry in the localTimeEntries array.
         * @async
         */
        async handleAction(position, entryId) {
            const userId = this.user?.uid;
            if (!userId) throw new Error("User not logged in");

            // Work in chronological order for neighbor logic
            const sorted = [...this.localTimeEntries].sort(
                (a, b) => a.startTime - b.startTime
            );
            const idx = sorted.findIndex((e) => e.id === entryId);
            if (idx === -1) return;

            const current = sorted[idx];
            let startTime, endTime;
            let transferEndOfSession = false;

            // Collect DB writes and do them together at the end
            const writes = [];

            if (position === "before") {
                if (!current.startTime) return;

                // Adjust the startTime of the clicked item by +1 minute
                current.startTime += 60 * 1000;
                current.startTimeFormatted = this.formatUtcTimeToLocal(
                    current.startTime
                );
                this.recalculateDuration(current);

                // Persist current
                await this.$store.dispatch("updateTimeEntryInterval", {
                    userId,
                    entryId: current.id,
                    startTime: current.startTime,
                    endTime: current.endTime,
                });

                // New entry fills the 1-minute gap just created
                endTime = current.startTime;
                startTime = endTime - 60 * 1000;

                // Keep continuity with previous neighbor if same session
                const prev = sorted[idx - 1];
                if (prev && !prev.endOfSession) {
                    prev.endTime = startTime;
                    prev.endTimeFormatted =
                        this.formatUtcTimeToLocal(startTime);
                    this.recalculateDuration(prev);
                    await this.$store.dispatch("updateTimeEntryInterval", {
                        userId,
                        entryId: prev.id,
                        startTime: prev.startTime,
                        endTime: prev.endTime,
                    });
                }
            } else if (position === "after") {
                if (!current.endTime) {
                    console.warn("No endTime for current entry.");
                    return;
                }

                // Pull current entry back by 1 minute
                current.endTime -= 60 * 1000;
                current.endTimeFormatted = this.formatUtcTimeToLocal(
                    current.endTime
                );
                this.recalculateDuration(current);

                // Persist current
                await this.$store.dispatch("updateTimeEntryInterval", {
                    userId,
                    entryId: current.id,
                    startTime: current.startTime,
                    endTime: current.endTime,
                });

                // New entry starts right after the shortened current
                startTime = current.endTime;
                endTime = startTime + 60 * 1000;

                // If current had an end-of-session, move it to the new entry
                if (current.endOfSession) {
                    current.endOfSession = false;
                    transferEndOfSession = true;
                    await this.$store.dispatch("updateTimeEntryFlag", {
                        userId,
                        entryId: current.id,
                        endOfSession: false,
                    });
                }

                // Keep continuity with next neighbor only if we did NOT insert a session boundary
                const next = sorted[idx + 1];
                if (next && !transferEndOfSession) {
                    next.startTime = endTime;
                    next.startTimeFormatted =
                        this.formatUtcTimeToLocal(endTime);
                    this.recalculateDuration(next);
                    await this.$store.dispatch("updateTimeEntryInterval", {
                        userId,
                        entryId: next.id,
                        startTime: next.startTime,
                        endTime: next.endTime,
                    });
                }
            } else {
                return;
            }

            // Create the new 1-minute entry (carry over endOfSession if we moved it)
            const newEntry = {
                projectId: null,
                startTime,
                endTime,
                ...(transferEndOfSession ? { endOfSession: true } : {}),
            };

            await this.$store.dispatch("addTimeEntry", { userId, newEntry });
        },

        /**
         * Formats plain entries into the data table structure.
         * Maps fields like startTime and endTime into a user-friendly format.
         *
         * @param {Array} entries - Array of plain time entries.
         * @returns {Array} Formatted entries for the data table.
         */
        formatPlainEntriesForDataTable(entries) {
            if (!Array.isArray(entries)) {
                console.warn(
                    "formatPlainEntriesForDataTable - entries should be an array"
                );
                return [];
            }
            return entries.map((entry) => {
                const project = this.projects.find(
                    (p) => p.projectId === entry.projectId
                );

                let hours = "0m";

                if (entry.startTime && entry.endTime) {
                    const durationMs = entry.endTime - entry.startTime;
                    const totalMinutes = Math.ceil(durationMs / (1000 * 60));
                    const h = Math.floor(totalMinutes / 60);
                    const m = totalMinutes % 60;
                    hours = h > 0 ? `${h}h ${m}min` : `${m}min`;
                }

                // Legacy placeholder string → null so autocomplete shows
                // placeholder text and typing does not require clearing first.
                const projectId =
                    !entry.projectId || entry.projectId === "Select Project"
                        ? null
                        : entry.projectId;

                return {
                    startTime: entry.startTime,
                    endTime: entry.endTime,
                    id: entry.id,
                    projectId,
                    description: entry.description || null,
                    projectName: project ? project.name : projectId,
                    startTimeFormatted: this.formatUtcTimeToLocal(
                        entry.startTime
                    ),
                    endTimeFormatted: entry.endTime
                        ? this.formatUtcTimeToLocal(entry.endTime)
                        : "Ongoing",
                    startTimePickerVisible: false,
                    endTimePickerVisible: false,
                    hours,
                    endOfSession: entry.endOfSession || false, // Include endOfSession from Firebase
                    overtime: entry.overtime === true,
                };
            });
        },
    },
    beforeMount() {
        this.localTimeEntries = this.formatPlainEntriesForDataTable(
            this.timeEntries
        );
    },
};
</script>
<style scoped>
.disabled-style {
    opacity: 0.5;
    color: #888;
}

.unstyled-button {
    background: none !important;
    box-shadow: none !important;
    outline: none !important;
    pointer-events: none;
    cursor: default !important;
}

.unstyled-button:hover,
.unstyled-button:focus,
.unstyled-button:active {
    background: none !important;
    box-shadow: none !important;
    outline: none !important;
}

th {
    text-align: center !important;
}

.v-text-field::v-deep(input) {
    font-size: 0.875rem !important;
}

.v-field {
    font-size: 14px !important;
}

.v-input {
    font-size: 0.875rem !important;
}

.v-select::v-deep(.v-select__selection-text) {
    font-size: 0.875rem !important;
}

.v-field::v-deep(.v-field__prepend-inner) {
    display: none !important;
}
.row-disabled {
    pointer-events: none; /* Disable interaction */
    opacity: 0.5; /* Dim the row */
    background-color: #f5f5f5; /* Optional: Give it a distinct color */
}

.disabled-style {
    color: #aaa; /* Dim the text for disabled fields */
    pointer-events: none;
}
</style>
